// apps/web/src/widgets/branch-variant/renderer.tsx
//
// Phase 7 — WIDG-11 + WIDG-09 BranchVariantRenderer (D-10..D-13 typed config).
// Phase 11.6-04 — size-indexed override render (REND-05 render half).
//
// Inline variant card display (D-12 no-modal-no-popover; ALL active variants
// render — multi-variant-per-option is v1 behavior, NOT a "default variant
// only" subset deferred to Phase 8). State shape
// `{ selectedVariantByOptionId: Record<OptionId, VariantId> }` (D-13; v1
// renderer does NOT consult this — the schema exists in the WidgetState union
// for a future Phase 8 Author UI that selects among multiple variants).
//
// Per UI-SPEC §"Branch-Variant Visual Contract": reads the source option-set
// state via RecipeStateContext; for each selected option, for each variant
// whose attachToOptionIds[] includes that option id, emits one inline card
// with VARIANT mini-title + border-l-4 accent stripe + override-field
// summary. Variants attached to multiple simultaneously-selected options
// de-duplicate by variant.id so a single shared variant renders ONE card,
// not N.
//
// Phase 11.6-04 size-indexed overrides (CONTEXT D1 / criterion 1's render half):
//   An override field DECLARED size-indexed in recipe.variantOverrideShapes
//   carries a per-size ARRAY. The <dd> for such a field resolves the value to
//   the currently-selected batch size (a resolved line) PLUS a compact per-size
//   strip (one cell per axis step). The active size is read CROSS-WIDGET from
//   the scaling-matrix widget's state by STEP-ID → POSITION:
//   `axisSelections[sizeAxisId]` is a STEP ID, mapped via
//   `axis.steps.findIndex(s => s.id === stepId)` (state holds step IDs, never
//   numeric indices). Scalar fields keep `label: value`; arrays NOT declared
//   size-indexed keep the existing "…" placeholder. Out-of-range / missing
//   cells render "—"; no size selected renders the full strip with NO active
//   cell. The active-size cell is emphasized non-color (font-semibold + an
//   accent left-marker + accent-soft bg — mirrors option-tier-matrix), NEVER
//   color alone (UI-SPEC §Accessibility item 2).
//
// Cross-widget read posture (summary-checklist precedent): ONE top-level
// RecipeStateContext hook; both the source option-set state AND any
// size-indexed field's scaling-widget state come from `ctx.states`. NO hook is
// called inside the variant/override `.map` loops (Rules of Hooks). The pure
// resolveSizeIndex / formatSizeCell helpers (format-override-value.ts) do the
// STEP-ID→position + bounded-cell stringification.
//
// XSS defense layer: NO raw-HTML sink, NO markdown library. The override-field
// <dd> value goes through format-override-value.ts which uses String() for
// primitives (React auto-escapes JSX text children) and the literal "…" / "—"
// placeholders for non-renderable / structured values. title=JSON.stringify is
// React-auto-escaped at the attribute layer. The size-indexed strip values flow
// through formatSizeCell/String — same posture.
//
// GENERALITY (C1): no recipe-specific vocabulary (size labels, temps, flavor
// nouns) is hard-coded — every rendered string flows from `recipe`/`config`
// DATA. The only literal copy strings are domain-free.
//
// Empty-state ladder (UI-SPEC line 278-279):
//   1. config.variants.length === 0       → "No variants declared." paragraph
//   2. no active variants (no source sel) → empty body (header alone)
//   3. variant active, overrideFields {}  → card without <dl>
import type { ScalingMatrixConfig, ScalingMatrixStep } from "@the-recipe-spread/shared";
import { useContext } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { RecipeStateContext } from "../recipe-state-provider.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import {
  formatSizeCell,
  renderOverrideValue,
  resolveSizeIndex,
  toTitleCase,
} from "./format-override-value.js";

// Resolved scaling read for a size-indexed override field: the axis's steps
// (positionally aligned with the override array) + the active step index. When
// the scaling widget / axis cannot be resolved, or no size is selected, `steps`
// is whatever was found (possibly empty) and `activeIdx` is -1 → "no active
// cell" render. Kept inline (no hook) so it runs inside the override .map.
interface ResolvedScaling {
  readonly steps: ReadonlyArray<ScalingMatrixStep>;
  readonly activeIdx: number;
}

export function BranchVariantRenderer({
  widget,
  config,
  recipe,
}: WidgetRendererProps<"branch-variant">): React.ReactElement {
  // D-13 + 11.6-04 cross-widget read: ONE top-level RecipeStateContext read (the
  // summary-checklist multi-source precedent) so BOTH the source option-set's
  // selected ids AND any size-indexed field's scaling-widget state come from the
  // same provider Map — NO hook inside the variant/override .map loops (Rules of
  // Hooks). The source widget MUST be an option-set instance in the same recipe
  // (Pass 2A does not yet enforce — Pass 2B will; T-07-10-06 disposition is
  // fail-soft).
  const ctx = useContext(RecipeStateContext);
  if (ctx === null) {
    throw new Error("<BranchVariantRenderer> must be rendered inside <RecipeStateProvider>");
  }

  const sourceState = ctx.states.get(config.sourceOptionSetWidgetId);
  const selectedIds: ReadonlyArray<string> =
    sourceState !== undefined && "selected" in sourceState ? sourceState.selected : [];

  // 11.6-04 — resolve a size-indexed field's axis steps + active index. Finds
  // the scaling-matrix widget by `scalingWidgetId` in recipe.widgets, reads its
  // typed config.axes, finds the axis by `sizeAxisId`, then reads the scaler's
  // persisted `axisSelections[sizeAxisId]` (a STEP ID) from ctx.states and maps
  // it to a position via resolveSizeIndex (findIndex). All fail-soft: a missing
  // widget / axis / selection yields empty steps or activeIdx -1.
  const resolveScaling = (scalingWidgetId: string, sizeAxisId: string): ResolvedScaling => {
    const scalingWidget = recipe.widgets?.find((w) => w.instanceId === scalingWidgetId);
    // config is typed `unknown` on the Widget shape; this renderer only reads
    // axes (a structural read), so a narrow cast to the scaling config shape is
    // the established cross-widget pattern. A non-scaling/misconfigured widget
    // degrades to undefined axis → empty steps → no active cell (fail-soft).
    const scalingConfig = scalingWidget?.config as ScalingMatrixConfig | undefined;
    const axis = scalingConfig?.axes.find((a) => a.id === sizeAxisId);
    const steps = axis?.steps ?? [];
    const scalerState = ctx.states.get(scalingWidgetId);
    const stepId =
      scalerState !== undefined && "axisSelections" in scalerState
        ? scalerState.axisSelections[sizeAxisId]
        : undefined;
    return { steps, activeIdx: resolveSizeIndex(steps, stepId) };
  };

  // CONTEXT D-12: for each selected option o, for each variant v whose
  // attachToOptionIds[] includes o.id, emit one card. A single variant
  // attached to multiple selected options must render ONCE — track seen
  // variant ids in a Set so the filter is single-pass O(n) and the de-dup
  // happens in fixture-declaration order. (T-07-10-08 mitigation.)
  const selectedIdSet = new Set(selectedIds);
  const seenVariantIds = new Set<string>();
  const activeVariants = config.variants.filter((v) => {
    if (seenVariantIds.has(v.id)) return false;
    const isActive = v.attachToOptionIds.some((id) => selectedIdSet.has(id));
    if (isActive) seenVariantIds.add(v.id);
    return isActive;
  });

  const labelId = `${widget.instanceId}-label`;

  return (
    <Card className="mt-8">
      <CardContent className="p-4">
        {/* <section aria-labelledby={...}> is intrinsically exposed as role="region" by the
        accessibility tree per HTML spec, so we do NOT need explicit role="region" here
        (biome lint/a11y/noRedundantRoles). UI-SPEC §"Branch-Variant Visual Contract" — the
        widget surface remains a region landmark for screen readers; getByRole("region") in
        tests still resolves via the implicit role on <section aria-labelledby>. */}
        <section aria-labelledby={labelId}>
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {widget.name}
          </h3>

          {config.variants.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--recipe-muted)]">No variants declared.</p>
          ) : (
            // When activeVariants is empty (no attached option selected), this
            // map renders nothing — the widget header above stays for layout
            // stability per UI-SPEC line 278.
            activeVariants.map((variant) => {
              const overrideEntries = Object.entries(variant.overrideFields);
              return (
                <div
                  key={variant.id}
                  className={
                    "mt-3 rounded-md bg-[var(--recipe-card)] " +
                    "border-l-4 border-l-[var(--recipe-accent)] " +
                    "border-y border-r border-y-[var(--recipe-line)] border-r-[var(--recipe-line)] " +
                    "p-3 flex flex-col gap-2"
                  }
                >
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-accent)]">
                    VARIANT
                  </div>
                  <div className="text-sm font-semibold text-[var(--recipe-ink)]">
                    {variant.label}
                  </div>
                  {overrideEntries.length > 0 ? (
                    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
                      {overrideEntries.map(([fieldKey, fieldValue]) => {
                        // 11.6-04: a field DECLARED size-indexed (recipe.
                        // variantOverrideShapes[fieldKey].kind === "size-indexed")
                        // whose value is an array renders the resolved-value +
                        // per-size strip. Everything else (scalar, undeclared
                        // array, non-array size-indexed) falls through to the
                        // existing renderOverrideValue path (scalar → value;
                        // undeclared array → "…").
                        const shape = recipe.variantOverrideShapes?.[fieldKey];
                        const isSizeIndexed =
                          shape?.kind === "size-indexed" && Array.isArray(fieldValue);
                        return (
                          <div key={fieldKey} className="contents">
                            <dt className="font-semibold text-[var(--recipe-muted)]">
                              {toTitleCase(fieldKey)}:
                            </dt>
                            {isSizeIndexed ? (
                              <SizeIndexedValue
                                values={fieldValue}
                                {...resolveScaling(shape.scalingWidgetId, shape.sizeAxisId)}
                              />
                            ) : (
                              (() => {
                                const rendered = renderOverrideValue(fieldValue);
                                return (
                                  <dd className="text-[var(--recipe-ink)]" title={rendered.title}>
                                    {rendered.text}
                                  </dd>
                                );
                              })()
                            )}
                          </div>
                        );
                      })}
                    </dl>
                  ) : null}
                </div>
              );
            })
          )}
        </section>
      </CardContent>
    </Card>
  );
}

// 11.6-04 — the size-indexed <dd>: a resolved line (the array entry at the
// active size index, or "—" out of range, with a muted size-qualifier when a
// size is selected) + a compact per-size strip. The strip renders one cell per
// axis step IN AXIS ORDER, capped at min(values.length, steps.length) so a
// length-drifted array never reads past either end (a size with no array entry
// shows "—" via formatSizeCell). The ACTIVE cell (i === activeIdx) is
// emphasized NON-COLOR: font-semibold + an accent left-marker + accent-soft bg
// (mirrors option-tier-matrix:210-212,270 — the colorblind/SR signal is the
// weight + marker, not the fill hue). No size selected (activeIdx < 0) → strip
// with NO active cell + NO resolved line.
function SizeIndexedValue({
  values,
  steps,
  activeIdx,
}: {
  values: ReadonlyArray<unknown>;
  steps: ReadonlyArray<ScalingMatrixStep>;
  activeIdx: number;
}): React.ReactElement {
  const hasActive = activeIdx >= 0;
  const activeStep = hasActive ? steps[activeIdx] : undefined;
  // Render one cell per step, but never past the array's end (length-drift): a
  // step with no positional array entry would only ever show "—", so cap the
  // strip at the shorter of the two. formatSizeCell still floors any in-strip
  // gap to "—".
  const cellCount = Math.min(values.length, steps.length);
  return (
    <dd className="flex flex-col gap-1 text-[var(--recipe-ink)]">
      {hasActive ? (
        <span className="text-sm">
          {formatSizeCell(values, activeIdx)}
          {activeStep !== undefined ? (
            <span className="ml-1 text-xs text-[var(--recipe-muted)]">({activeStep.label})</span>
          ) : null}
        </span>
      ) : null}
      <span className="flex flex-wrap gap-2">
        {steps.slice(0, cellCount).map((step, i) => {
          const isActive = i === activeIdx;
          return (
            <span
              key={step.id}
              className={
                isActive
                  ? "flex flex-col rounded-sm border-l-2 border-l-[var(--recipe-accent)] bg-[var(--recipe-accent-soft)] px-2 py-0.5 font-semibold"
                  : "flex flex-col rounded-sm border-l-2 border-l-[var(--recipe-line)] px-2 py-0.5"
              }
            >
              <span className="text-xs text-[var(--recipe-muted)]">{step.label}</span>
              <span className="text-sm">{formatSizeCell(values, i)}</span>
            </span>
          );
        })}
      </span>
    </dd>
  );
}
